// RMS dashboards (.claude/features/12900-rms-dashboards.md): a dashboard is a saved
// view (`rms_views`) of type "dashboard" (#12953) whose config names a record
// kind and a layout of tabs, each with its own filters, of widgets on a 12-column grid. These types
// mirror the backend's pydantic model; the hooks read and write dashboards
// through the views API. The bootstrap lists dashboards beside the data views
// (the strip shows them); the list page and the dashboard page read their own
// list, which carries the same rows.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";
import type { EntityKind, FrontendFilter } from "../Dashboard/types";
import {
  createView,
  defaultViewQueryKey,
  updateView,
  useDeleteRmsView,
  useUpdateRmsView,
  viewsResponseError,
  DASHBOARD_VIEW_TYPE,
  type RmsView,
  type TableConfig,
} from "../Data/views";
import { parseDashboard } from "./dashboardShape";
import { mergeSavedView } from "../ViewStrip/pinning";
import { RMS_BOOTSTRAP_QUERY_KEY, setBootstrapViews } from "../Data/bootstrap";
import { createViewSlug } from "../Dashboard/utils/slugUtils";
import type { ComponentSummary } from "../Components/libraryApi";
import type { Color } from "../Components/types";
import { keepComponentSummaries } from "./widgetSummaries";
import { isTemporal, type Temporal } from "@/utils/temporal";
import type { ViewLayoutKind } from "../Components/viewSummary";

export interface DashboardWidget {
  id: string;
  component_id: string;
  /** The revision the widget renders (rms-dashboards, *Card versions*), moved only on Accept. */
  revision_id: string;
  /** A newer revision the editor kept the widget off (Keep current); absent on most widgets. */
  declined_revision_id?: string | null;
  /** Column the widget starts in, 0-based on the 12-column grid. */
  x: number;
  /** Row the widget starts in, 0-based, in row units. */
  y: number;
  /** Width in columns, 1..12. */
  w: number;
  /** Height in row units, at least 1. */
  h: number;
  /**
   * How the widget got its size (dashboard assistant polish, requirement 5): "auto" until the
   * editor fits it to its first render, "fitted" after that, "fixed" when a person or the
   * assistant chose it. Absent on a widget saved before sizing existed, which reads as fixed.
   */
  sizing?: WidgetSizing;
  /**
   * The least size the content fit may give an auto widget, per axis (#14923): a widget landed
   * from a planned layout takes the plan's size as its floor and grows past it. The server
   * applies it to every fit; absent on an axis the plan left to the fit.
   */
  min_w?: number;
  min_h?: number;
  /**
   * Whether a person or the assistant chose the widget's place (a drop, a move, an add given
   * both x and y). Only a widget the insertion placed (false) is inserted again when its kind is
   * recorded. Absent on a widget saved before the flag existed, which was placed by hand.
   */
  fixed_position?: boolean;
  /**
   * Whether a person moved or resized the widget on the editor, or dropped it at a chosen cell
   * (dashboard-working-status-and-free-movement requirement 3): the server sets it from the
   * page's own ops only, and nothing clears it (Auto adjust only closes empty rows, #15235).
   * Absent reads as false.
   */
  hand_placed?: boolean;
  /**
   * The dashboard assistant added the widget since the dashboard was last saved (polish-3
   * requirement 10): the editor marks it "Added by assistant". Only a draft carries it; a save
   * drops it.
   */
  added_by?: "assistant";
  /**
   * How this placement looks where it differs from its library original (#13714 L1.1): painted
   * on the rendered document on the way out (`applyAppearance`), never into the render cache, and
   * kept by a save.
   */
  appearance?: WidgetAppearance | null;
}

/** One placement's own look; each field shows on one kind of widget only (title on any). */
export interface WidgetAppearance {
  title?: string;
  color?: Color;
  /** A bar chart's. */
  orientation?: "vertical" | "horizontal";
  /** A bar chart's bar width: a word, or since round 9 a percent of the category (5 to 100). */
  bar_width?: "auto" | "wide" | "narrow" | number;
  /**
   * A chart's looks as numbers and positions (round 9, S3.1), painted on the chart types that
   * draw each one.
   */
  line_width?: number;
  point_size?: number;
  hole?: number;
  legend?: "auto" | "top" | "bottom" | "left" | "right" | "none";
  labels?: "auto" | "values" | "none";
  /** A table's. */
  header_tone?: "default" | "muted" | "strong";
  /** A table's first local sort, or a chart's categories by value (round 9). */
  sort?:
    | { column: string; direction: "asc" | "desc" }
    | "none"
    | "asc"
    | "desc";
  /** A metric's scalar, 0 to 4 decimals. */
  precision?: number;
}

export type WidgetSizing = "auto" | "fitted" | "fixed";

export interface DashboardTab {
  id: string;
  name: string;
  widgets: DashboardWidget[];
  /**
   * The tab's own clauses (dashboard-assistant-polish-4 requirement 5): they narrow this tab's
   * widgets only. The server serves every tab with its list; a tab the page has only just added
   * has none yet.
   */
  filters?: DashboardFilter[];
}

export interface DashboardConfig {
  tabs: DashboardTab[];
}

/** The kinds a dashboard's clause can be over: a record kind, interactions or documents. */
export type DashboardFilterKind = ViewLayoutKind;

/**
 * One of a dashboard's clauses (#12938): a saved view's name-keyed clause and the kind whose
 * reads it narrows. On a single-kind dashboard a clause with no kind is the dashboard's kind.
 */
export type DashboardFilter = FrontendFilter & {
  entity_kind?: DashboardFilterKind;
};

/** A dashboard's description holds at most this many characters (#13380). */
export const DASHBOARD_DESCRIPTION_MAX = 2000;

export interface DashboardViewConfig {
  /** Null on a mixed dashboard ("Mixed records"): its filters may be over any kind. */
  entity_kind: EntityKind | null;
  table_config?: TableConfig | null;
  dashboard: DashboardConfig;
  /** What the dashboard should track, as its creator wrote it; the assistant builds from it. */
  description?: string;
  /** The tile's icon key; blank or absent, the Dashboards glyph (polish-4 requirement 16). */
  icon?: string;
  /** The tile's colour key; blank or absent, the neutral tile. */
  color?: string;
}

/** A widget a save took back into the library, as the save's answer names it (#13380). */
export interface RestoredWidget {
  component_id: string;
  name: string;
}

/** The widgets a save restored to the library: none when the answer names none. */
export const restoredWidgetsOf = (written: DashboardView): RestoredWidget[] => {
  const restored = (written as { restored_widgets?: unknown }).restored_widgets;
  return Array.isArray(restored)
    ? restored.filter(
        (r): r is RestoredWidget =>
          typeof r === "object" &&
          r !== null &&
          typeof (r as RestoredWidget).name === "string"
      )
    : [];
};

export type DashboardView = Omit<RmsView, "config"> & {
  config: DashboardViewConfig;
  /**
   * The list's row carries the summary of every component the widgets place (requirement 15 of
   * .claude/features/dashboard-assistant-polish.md); absent on a row a write answered.
   */
  widget_summaries?: ComponentSummary[] | null;
  /**
   * For an editor, the dashboard's widgets that stopped rendering, in layout order (round 10 U6,
   * .claude/features/14112-dashboard-maintenance.md). Absent for a viewer and on a row a write answered.
   */
  failing_widget_ids?: string[] | null;
};

/**
 * Where the viewer's default view comes from (#12953, #13052): their own choice, else the
 * organization's default, else the leftmost view of the viewer's strip. "seeded" is what a
 * backend from before #13052 names that last step (the seeded Overview), for the minutes of a
 * deploy when this bundle can reach one: it is read as the strip's, not as a chosen default.
 */
export type DefaultViewSource = "user" | "organization" | "strip" | "seeded";

/** Whether the default was chosen (the viewer's or the organization's) rather than the strip's. */
export const isExplicitDefault = (source: DefaultViewSource): boolean => {
  switch (source) {
    case "user":
    case "organization":
      return true;
    case "strip":
    case "seeded":
      return false;
    default: {
      const unknown: never = source;
      throw new Error(`Unknown default view source: ${String(unknown)}`);
    }
  }
};

/** The view bare /rms opens: a dashboard or a data view, whichever the default is. */
export interface DefaultView {
  view: RmsView | null;
  source: DefaultViewSource;
  /** The organization's default as stored, whatever the viewer's own choice (#12953). */
  organization_view_id?: number | null;
}

/** The one tab a new dashboard starts with. */
export const FIRST_TAB: DashboardTab = {
  id: "tab-1",
  name: "Overview",
  widgets: [],
};

export const dashboardsQueryKey = (orgId: string | null | undefined) =>
  ["rms-dashboards", orgId] as const;
// Defined beside the views' delete, which reads it again when a view goes.
export { defaultViewQueryKey };

/**
 * Writes a dashboard's freshly read row over the list's copy (#15511): a write that answers with
 * something other than the row (a share answers with the sharing status) leaves the list's copy,
 * and so the Visibility chip and the page's head, on the old row otherwise. A list without that
 * dashboard, or one never read, is left as it is.
 */
export const setCachedDashboard = (
  queryClient: QueryClient,
  orgId: string | null | undefined,
  row: RmsView
): void => {
  const key = dashboardsQueryKey(orgId);
  const listed = queryClient.getQueryData<DashboardView[]>(key);
  if (!listed?.some((d) => d.id === row.id)) return;
  const fresh = parseDashboard(row);
  if (!fresh) return;
  // What only the list carries (the failing widgets, the widgets' summaries) stays.
  queryClient.setQueryData<DashboardView[]>(key, (old) =>
    old?.map((d) => (d.id === fresh.id ? { ...d, ...fresh } : d))
  );
};

const viewsUrl = `${BASE_API_URL_V2}/api/v2/rms/views`;
const defaultViewUrl = `${BASE_API_URL_V2}/api/v2/rms/default-view`;
const organizationDefaultViewUrl = `${BASE_API_URL_V2}/api/v2/rms/organizations/default-view`;

const fetchDashboards = async (signal?: AbortSignal) => {
  const response = await fpFetch(
    `${viewsUrl}?view_type=${DASHBOARD_VIEW_TYPE}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      signal,
    }
  );
  if (!response.ok)
    throw await viewsResponseError(response, "fetch dashboards");
  const rows = (await response.json()) as RmsView[];
  return rows.flatMap((row) => {
    const parsed = parseDashboard(row);
    return parsed ? [parsed] : [];
  });
};

/** A written row read back as a dashboard; a row that is not one is a server fault. */
const asDashboard = (row: RmsView): DashboardView => {
  const parsed = parseDashboard(row);
  if (!parsed)
    throw new Error("The server answered with a view that is not a dashboard");
  return parsed;
};

/**
 * Why a dashboard's address shows no dashboard (dashboard-assistant-polish-9, S1): it is not
 * shared with the viewer, and the server names its owner to ask (null when the owner cannot be
 * resolved); it is shared with them (or theirs) but their organization does not have dashboards
 * yet; or there is no such dashboard for them.
 */
export type DashboardAccess =
  | {
      kind: "no_access";
      ownerName: string | null;
      /** When the viewer asked for access, while that request is open (round 10 U4). */
      requestedAt: Temporal | null;
      /** Nobody can be asked: the owner left and the dashboard has no editor. */
      noOneToAsk: boolean;
    }
  | {
      kind: "no_component_access";
      ownerName: string | null;
      /** The viewer made it: it was not shared with them. */
      isOwner: boolean;
    }
  | { kind: "unavailable" };

/** The blocked read's body (and the access request's answer), as the page holds it. */
const blockedAccess = (body: {
  code?: unknown;
  owner_name?: unknown;
  requested_at?: unknown;
  no_one_to_ask?: unknown;
  is_owner?: unknown;
}): DashboardAccess => {
  const ownerName =
    typeof body.owner_name === "string" ? body.owner_name : null;
  switch (body.code) {
    case "no_access":
      return {
        kind: "no_access",
        ownerName,
        requestedAt: isTemporal(body.requested_at) ? body.requested_at : null,
        noOneToAsk: body.no_one_to_ask === true,
      };
    case "no_component_access":
      return {
        kind: "no_component_access",
        ownerName,
        isOwner: body.is_owner === true,
      };
    default:
      return { kind: "unavailable" };
  }
};

const dashboardAccessUrl = (publicId: string): string =>
  `${viewsUrl}/by-public-id/${encodeURIComponent(publicId)}`;

const dashboardAccessQueryKey = (
  orgId: string | null | undefined,
  publicId: string
) => ["rms-dashboard-access", orgId, publicId] as const;

const fetchDashboardAccess = async (
  publicId: string,
  signal?: AbortSignal
): Promise<DashboardAccess> => {
  const response = await fpFetch(dashboardAccessUrl(publicId), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
    expectedStatuses: [403, 404],
  });
  if (response.status === 403) {
    // The body is read once, here: a 403 never falls through to another read.
    return blockedAccess(await response.json().catch(() => ({})));
  }
  // A 404, or a view the viewer can read that is not on their dashboards list (not a
  // dashboard, or one the list could not draw): nothing this page can open.
  if (response.status === 404 || response.ok) return { kind: "unavailable" };
  throw await viewsResponseError(response, "fetch the dashboard");
};

/**
 * What the views route says about a dashboard the viewer's list does not hold. Asked once per
 * address, with no retry: the answer is a fact about access, not a transient fault.
 */
export function useDashboardAccess(
  publicId: string,
  { enabled }: { enabled: boolean }
) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: dashboardAccessQueryKey(orgId, publicId),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchDashboardAccess(publicId, signal),
    enabled: enabled && Boolean(orgId),
    retry: false,
  });
}

const requestDashboardAccess = async (
  publicId: string
): Promise<DashboardAccess> => {
  const response = await fpFetch(
    `${dashboardAccessUrl(publicId)}/access-requests`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // A refused send is the route's 502: answered as it is, never sent again by a retry.
      expectedStatuses: [502],
    }
  );
  if (!response.ok)
    throw await viewsResponseError(response, "send the request");
  return blockedAccess(await response.json());
};

/**
 * Asks the dashboard's owner for access (round 10 U4). The answer is the blocked read's, with the
 * request in it, so the page's access state is replaced by it rather than read again.
 */
export function useRequestDashboardAccess(publicId: string) {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => requestDashboardAccess(publicId),
    onSuccess: (answer) =>
      queryClient.setQueryData(
        dashboardAccessQueryKey(orgId, publicId),
        answer
      ),
  });
}

const fetchDefaultView = async (signal?: AbortSignal): Promise<DefaultView> => {
  const response = await fpFetch(defaultViewUrl, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  });
  if (!response.ok)
    throw await viewsResponseError(response, "fetch the default view");
  return (await response.json()) as DefaultView;
};

/**
 * The dashboards list's read, shared by its hook and the app's idle prefetch of the RMS. The
 * widgets' component summaries its rows carry are kept where each widget reads its own, so a
 * dashboard opened from the list reads none.
 */
export const dashboardsQuery = (
  orgId: string | null | undefined,
  queryClient: QueryClient
) => ({
  queryKey: dashboardsQueryKey(orgId),
  queryFn: async ({ signal }: { signal?: AbortSignal }) => {
    const dashboards = await fetchDashboards(signal);
    for (const dashboard of dashboards)
      keepComponentSummaries(queryClient, orgId, dashboard.widget_summaries);
    return dashboards;
  },
});

/** Every dashboard the viewer can open, most recently updated first. */
export function useDashboards({ enabled = true }: { enabled?: boolean } = {}) {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  return useQuery({
    ...dashboardsQuery(orgId, queryClient),
    enabled: enabled && Boolean(orgId),
  });
}

/**
 * The viewer's default view, which bare /rms opens (#12953, #13052). For a viewer with component access only:
 * the server refuses anyone else, so the caller passes `enabled` from the bootstrap's gate.
 */
export function useDefaultView({ enabled = true }: { enabled?: boolean } = {}) {
  const orgId = useOrgId();
  return useQuery({
    ...defaultViewQuery(orgId),
    enabled: enabled && Boolean(orgId),
  });
}

/** The default view's read, shared by its hook and the app's idle prefetch of it. */
export const defaultViewQuery = (orgId: string | null | undefined) => ({
  queryKey: defaultViewQueryKey(orgId),
  queryFn: ({ signal }: { signal?: AbortSignal }) => fetchDefaultView(signal),
  retry: false,
});

/** One write to a default: the viewer's own or the organization's, set (a view id) or cleared. */
const writeDefault = async (
  url: string,
  viewId: number | null,
  what: string
): Promise<void> => {
  const response = await fpFetch(url, {
    method: viewId === null ? "DELETE" : "PUT",
    headers: { "Content-Type": "application/json" },
    ...(viewId === null ? {} : { body: JSON.stringify({ view_id: viewId }) }),
  });
  if (!response.ok) throw await viewsResponseError(response, what);
};

/** After a write that changes which view bare /rms opens, the default is read again. */
function useRereadDefault(): () => Promise<void> {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: defaultViewQueryKey(orgId) });
}

/** After setting the organization's default: the server also pinned the view and moved it to the
 * front of the organization's layout (#13052 correction), so the strip (in the bootstrap) is read
 * again with the default. Its answer is the organization's default, not the viewer's effective
 * one, so the viewer's default is read rather than taken from it. */
function useRereadDefaultAndStrip(): () => Promise<void> {
  const rereadDefault = useRereadDefault();
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      rereadDefault(),
      queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY }),
    ]);
  };
}

/** The viewer's own default set to `viewId`: the server answers the default it now resolves. */
const putUserDefault = async (viewId: number): Promise<DefaultView> => {
  const response = await fpFetch(defaultViewUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ view_id: viewId }),
  });
  if (!response.ok)
    throw await viewsResponseError(response, "set the default view");
  return (await response.json()) as DefaultView;
};

/**
 * Makes a view the viewer's own default (`mutate(viewId)`); the server pins it and moves it to
 * the front of the viewer's strip. The answer is the viewer's default, so it is written straight
 * into the default's cache (no second read, and the glyph moves the moment the answer lands, not
 * after the strip does); only the strip, in the bootstrap, is read again.
 */
export function useSetDefaultView() {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const rereadDefault = useRereadDefault();
  return useMutation({
    mutationFn: putUserDefault,
    onSuccess: (answer) =>
      queryClient.setQueryData(defaultViewQueryKey(orgId), answer),
    // A refused write leaves the default as the server holds it: read it again.
    onError: rereadDefault,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY }),
  });
}

/** Clears the viewer's own default: it falls back to the organization's, else the strip's. */
export function useClearDefaultView() {
  const reread = useRereadDefault();
  return useMutation({
    mutationFn: () =>
      writeDefault(defaultViewUrl, null, "clear the default view"),
    onSettled: reread,
  });
}

/** Makes a view the organization's default (organization admins and managers); the server pins
 * it and moves it to the front of the organization's strip layout. */
export function useSetOrganizationDefaultView() {
  const reread = useRereadDefaultAndStrip();
  return useMutation({
    mutationFn: (viewId: number) =>
      writeDefault(
        organizationDefaultViewUrl,
        viewId,
        "set the organization's default view"
      ),
    onSettled: reread,
  });
}

/** Where a view opens: a dashboard at its page, a data view at its slug. */
export const viewAddress = (view: RmsView): string =>
  view.view_type === DASHBOARD_VIEW_TYPE
    ? `/rms/dashboards/${view.public_id}`
    : `/rms/view/${createViewSlug(view)}`;

export interface CreateDashboardInput {
  name: string;
  /** What the dashboard should track; empty or absent, none is stored. */
  description?: string;
  /** The tile's icon and colour keys; empty or absent, none is stored. */
  icon?: string;
  color?: string;
}

/** A created dashboard. */
export interface CreatedDashboard {
  dashboard: DashboardView;
  /** The row as the server answered it, which the bootstrap's views hold. */
  view: RmsView;
}

/**
 * Creates a private dashboard with one empty tab and no record kind of its own: its filters may
 * be over any kind (#12938), so the list shows it as mixed records. It never asks the server to
 * start the assistant's first turn (dashboard-menus-prefill-description requirement 2): the
 * description waits in the composer for the person to send. The created dashboard goes into the
 * list's cache and the bootstrap's views at once, so its page opens without waiting for either to
 * be read again; both are read again behind it.
 */
export function useCreateDashboard() {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      description,
      icon,
      color,
    }: CreateDashboardInput): Promise<CreatedDashboard> => {
      const config: Omit<DashboardViewConfig, "table_config"> = {
        entity_kind: null,
        dashboard: { tabs: [FIRST_TAB] },
        ...(description ? { description } : {}),
        // An untouched tile sends nothing: the dashboard wears the Dashboards glyph.
        ...(icon ? { icon } : {}),
        ...(color ? { color } : {}),
      };
      const row = await createView({
        name,
        view_type: DASHBOARD_VIEW_TYPE,
        config,
      });
      return { dashboard: asDashboard(row), view: row };
    },
    // Not awaited: the dialog navigates as soon as the dashboard exists.
    onSuccess: ({ dashboard, view }) => {
      queryClient.setQueryData<DashboardView[]>(
        dashboardsQueryKey(orgId),
        (rows) => [
          dashboard,
          ...(rows ?? []).filter((r) => r.id !== dashboard.id),
        ]
      );
      setBootstrapViews(queryClient, orgId ?? undefined, (views) => [
        ...views.filter((v) => v.id !== view.id),
        view,
      ]);
      void queryClient.invalidateQueries({
        queryKey: dashboardsQueryKey(orgId),
      });
      void queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY });
    },
  });
}

export interface CopyDashboardInput {
  name: string;
  /** The copy's whole config: the original's as saved ("Make a copy"), or with the edits on
   *  screen ("Save as new dashboard"). */
  config: DashboardViewConfig;
}

/** Creates a private copy of a dashboard ("Make a copy", "Save as new dashboard"). */
export function useCopyDashboard() {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  return useMutation({
    // A dashboard keeps no table config (it shows widgets, not a table), so the copy carries
    // what a dashboard is: its type, its kind and its layout (each tab with its filters), and
    // what it says of itself: its description, icon and colour (polish-4 requirement 16).
    mutationFn: async ({ name, config }: CopyDashboardInput) =>
      asDashboard(
        await createView({
          name,
          view_type: DASHBOARD_VIEW_TYPE,
          config: {
            entity_kind: config.entity_kind,
            dashboard: config.dashboard,
            ...(config.description ? { description: config.description } : {}),
            ...(config.icon ? { icon: config.icon } : {}),
            ...(config.color ? { color: config.color } : {}),
          },
        })
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: dashboardsQueryKey(orgId) }),
        queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY }),
      ]),
  });
}

export interface UpdateDashboardInput {
  view: DashboardView;
  /** The whole config: the server merges config keys shallowly, so the
   *  `dashboard` block always travels complete. A widget with no place and no size is placed
   *  by the server (`withWidgetOnFirstTab`). */
  config: DashboardViewConfig | DashboardWriteConfig;
  name?: string;
  /**
   * The lock token the edit started from. A page holding an open edit pins it, so a refetch
   * that brings another person's newer row cannot lend this write their token; without it the
   * write carries the newest token the list holds.
   */
  token?: string;
}

/**
 * Writes a dashboard with the optimistic lock (`updated_at`). A 409 reaches the
 * caller as `ViewConflictError` (`isViewSaveConflict`), with no toast, so the
 * caller can keep its local edits and say what happened.
 */
export function useUpdateDashboard() {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const listKey = dashboardsQueryKey(orgId);
  return useMutation({
    // One write at a time, so a second write reads the lock token the first
    // one's answer brought rather than the one it started from.
    scope: { id: "rms-dashboard-update" },
    mutationFn: async ({ view, config, name, token }: UpdateDashboardInput) => {
      const current = queryClient
        .getQueryData<DashboardView[]>(listKey)
        ?.find((d) => d.id === view.id);
      const row = await updateView(view.id, {
        ...(name !== undefined ? { name } : {}),
        // A dashboard's kind never changes, and a mixed one's null kind is not sent: the
        // server merges config keys shallowly, so the stored null stays.
        config: { ...config, entity_kind: config.entity_kind ?? undefined },
        updated_at: token ?? current?.updated_at ?? view.updated_at,
      });
      // The bootstrap's copy of the view (the strip's) takes the saved row at once, as every
      // view write's does: its `updated_at` is the token a rename from the strip sends next, and
      // an older copy would be refused as a conflict.
      setBootstrapViews(queryClient, orgId, (views) =>
        views.map((v) => (v.id === row.id ? mergeSavedView(v, row) : v))
      );
      return asDashboard(row);
    },
    // The list shows the write at once (a board card stays in the lane it was
    // dropped on). A read in flight is cancelled so its older answer cannot
    // land over the write; a refusal puts the list back as it was.
    onMutate: async ({ view, config, name }: UpdateDashboardInput) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<DashboardView[]>(listKey);
      // A write the server still has to place widgets for shows when its answer lands.
      if (isPlacedConfig(config))
        queryClient.setQueryData<DashboardView[]>(listKey, (old) =>
          old?.map((d) =>
            d.id === view.id
              ? { ...d, config, ...(name !== undefined ? { name } : {}) }
              : d
          )
        );
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous)
        queryClient.setQueryData<DashboardView[]>(listKey, context.previous);
    },
    // The server's row carries the new `updated_at`: the next write's token.
    onSuccess: (saved) => {
      queryClient.setQueryData<DashboardView[]>(listKey, (old) =>
        old?.map((d) => (d.id === saved.id ? saved : d))
      );
      // And the bootstrap is re-read for what the row does not carry (the widget summaries).
      void queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: listKey }),
  });
}

/**
 * A dashboard's config with `description` as its description. A blank one is sent as "", which
 * clears it: the server merges a write's config keys shallowly, so an absent key would keep it.
 */
export const describedConfig = (
  config: DashboardViewConfig,
  description: string
): DashboardViewConfig => ({ ...config, description });

/** What a write of the list row changes: the name, the description, or both. */
export interface DashboardRowPatch {
  name?: string;
  description?: string;
}

/**
 * Writes a dashboard's list row at once (its name, its description) through the views' own update,
 * the one the strip's rename uses, so the two cannot drift: that write renames the strip's tab at
 * once. The list (which the dashboard page reads its name and description from) takes the patch
 * here at once as well, and a refused save puts the old row back in both, where the update has
 * already said why. Never a draft change (dashboard-menus-prefill-description requirement 3).
 */
export function useWriteDashboardRow(): (
  view: DashboardView,
  patch: DashboardRowPatch
) => Promise<void> {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const update = useUpdateRmsView();
  const listKey = dashboardsQueryKey(orgId);
  return async (
    view: DashboardView,
    { name, description }: DashboardRowPatch
  ) => {
    // A read in flight would land its older row over the write.
    await queryClient.cancelQueries({ queryKey: listKey });
    const previous = queryClient.getQueryData<DashboardView[]>(listKey);
    const current = previous?.find((d) => d.id === view.id) ?? view;
    const config =
      description !== undefined
        ? describedConfig(current.config, description)
        : undefined;
    queryClient.setQueryData<DashboardView[]>(listKey, (old) =>
      old?.map((d) =>
        d.id === view.id
          ? {
              ...d,
              ...(name !== undefined ? { name } : {}),
              ...(config ? { config } : {}),
            }
          : d
      )
    );
    try {
      // Awaited per call, so each write gets its own answer even when a second one starts before
      // the first has landed.
      const saved = await update.mutateAsync({
        viewId: view.id,
        payload: {
          ...(name !== undefined ? { name } : {}),
          // A mixed dashboard's null kind is not sent, as the dashboard's own update does not.
          ...(config
            ? {
                config: {
                  ...config,
                  entity_kind: config.entity_kind ?? undefined,
                },
              }
            : {}),
          updated_at: current.updated_at,
        },
      });
      // The server's row carries the new `updated_at`: the next write's token, at once rather
      // than when the refetch below lands.
      const parsed = parseDashboard(saved);
      if (parsed)
        queryClient.setQueryData<DashboardView[]>(listKey, (old) =>
          old?.map((d) => (d.id === parsed.id ? parsed : d))
        );
    } catch {
      if (previous)
        queryClient.setQueryData<DashboardView[]>(listKey, previous);
    } finally {
      void queryClient.invalidateQueries({ queryKey: listKey });
    }
  };
}

/** Renames a dashboard from the Dashboards list, the strip or its head: the row write's name. */
export function useRenameDashboard(): (
  view: DashboardView,
  name: string
) => void {
  const write = useWriteDashboardRow();
  return (view: DashboardView, name: string) => {
    void write(view, { name });
  };
}

/**
 * Deletes a dashboard from the Dashboards list through the views' own delete, which reports the
 * outcome in a toast: on success the strip drops its tab (the bootstrap is read again) and the
 * list drops its row; on a failure both stay.
 */
export function useDeleteDashboard(): (view: DashboardView) => void {
  const orgId = useOrgId();
  const queryClient = useQueryClient();
  const remove = useDeleteRmsView();
  const listKey = dashboardsQueryKey(orgId);
  return (view: DashboardView) => {
    remove.mutate(
      { viewId: view.id, name: view.name },
      {
        onSuccess: () => {
          queryClient.setQueryData<DashboardView[]>(listKey, (old) =>
            old?.filter((d) => d.id !== view.id)
          );
          void queryClient.invalidateQueries({ queryKey: listKey });
        },
      }
    );
  };
}

export { dashboardWidgetCount, parseDashboard } from "./dashboardShape";

/**
 * A widget a save adds with no place and no size (dashboard-assistant-polish-4 requirement 4): the
 * server inserts it by its class at its kind's default size, sized automatically, so the editor
 * fits it to what it shows the first time it opens.
 */
export type UnplacedWidget = Pick<
  DashboardWidget,
  "id" | "component_id" | "revision_id"
>;

/** A dashboard config as a write may send it: a tab may carry widgets the server places. */
export interface DashboardWriteConfig
  extends Omit<DashboardViewConfig, "dashboard"> {
  dashboard: {
    tabs: Array<
      Omit<DashboardTab, "widgets"> & {
        widgets: Array<DashboardWidget | UnplacedWidget>;
      }
    >;
  };
}

/** Whether every widget of a write has its place and size, as the page's caches hold them. */
const isPlacedConfig = (
  config: DashboardViewConfig | DashboardWriteConfig
): config is DashboardViewConfig =>
  config.dashboard.tabs.every((tab) =>
    tab.widgets.every((widget) => "x" in widget)
  );

/**
 * The dashboard's config with a widget appended to its first tab, with no place and no size for the
 * server to give it. The whole config comes back, because a dashboard is always written whole
 * (`UpdateDashboardInput`).
 */
export function withWidgetOnFirstTab(
  config: DashboardViewConfig,
  widget: UnplacedWidget
): DashboardWriteConfig {
  const [first, ...rest] = config.dashboard.tabs;
  const tab: DashboardTab = first ?? { ...FIRST_TAB };
  const added: UnplacedWidget = {
    id: widget.id,
    component_id: widget.component_id,
    revision_id: widget.revision_id,
  };
  return {
    ...config,
    dashboard: {
      ...config.dashboard,
      tabs: [{ ...tab, widgets: [...tab.widgets, added] }, ...rest],
    },
  };
}
